<template>
  <el-form label-position="top">
    <!-- Sender Type Selection -->
    <el-form-item label="Тип отправителя" :class="{ 'is-error': showErrors && !senderType }">
      <el-select v-model="senderType" placeholder="Выберите тип отправителя">
        <el-option
          v-for="(label, value) in senderTypeLabels"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <div v-if="showErrors && !senderType" class="el-form-item__error">Пожалуйста, выберите тип отправителя</div>
    </el-form-item>

    <!-- Selection and Upload Row -->
    <div class="selection-row">
      <el-form-item v-if="sendersList.length > 0 && !isStandalone" label="Использовать отправителя из прошлых заказов" class="selection-item">
        <el-select v-model="selectedSenderComputed" placeholder="Выберите отправителя">
          <el-option
            :key="'new'"
            label="Новый отправитель"
            :value="null"
          />
          <el-option
            v-for="sender in sendersList"
            :key="sender.id"
            :label="sender.name"
            :value="sender"
          />
        </el-select>
      </el-form-item>
      
      <el-form-item class="selection-item">
        <div class="upload-container">
          <el-upload
            action=""
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="beforeUpload"
            :limit="1"
            :file-list="fileList"
            :on-remove="handleRemoveFile"
            ref="uploadRef"
          >
            <el-button type="primary">Загрузить карточку</el-button>
            <template #tip>
              <div class="el-upload__tip">Загрузите карточку {{senderTypeLabelsGentiveCase[senderType as SenderTypeEnum]}} - наш сервис автоматически заполнит все поля</div>
            </template>
          </el-upload>
          <div v-if="isProcessingFile" class="loading-overlay">
            <el-icon class="loading-icon"><Loading /></el-icon>
            <div class="loading-text">Файл обрабатывается</div>
          </div>
        </div>
      </el-form-item>
    </div>

    <!-- Sender Fields -->
    <div class="sender-fields">
      <el-form-item label="Наименование" :class="{ 'is-error': showErrors && !name }">
        <el-input v-model="name" />
        <div v-if="showErrors && !name" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Наименование (англ.)">
        <el-input v-model="nameEn" />
        <div v-if="showErrors && !nameEn" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="ИНН" :class="{ 'is-error': showErrors && !inn }">
        <el-input v-model="inn" />
        <div v-if="showErrors && !inn" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Наименование банка" :class="{ 'is-error': showErrors && !bankName }">
        <el-input v-model="bankName" />
        <div v-if="showErrors && !bankName" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Наименование банка (англ.)">
        <el-input v-model="bankNameEn" />
        <div v-if="showErrors && !bankNameEn" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="БИК" :class="{ 'is-error': showErrors && !bankBik }">
        <el-input v-model="bankBik" />
        <div v-if="showErrors && !bankBik" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Расчетный счет" :class="{ 'is-error': showErrors && !bankCurrentAccount }">
        <el-input v-model="bankCurrentAccount" />
        <div v-if="showErrors && !bankCurrentAccount" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Корреспондентский счет">
        <el-input v-model="bankCorrespondentAccount" />
        <div v-if="showErrors && !bankCorrespondentAccount" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Юридический адрес">
        <el-input v-model="entityAddress" />
        <div v-if="showErrors && !entityAddress" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>
      <el-form-item label="Юридический адрес (англ.)">
        <el-input v-model="entityAddressEn" />
        <div v-if="showErrors && !entityAddressEn" class="el-form-item__error">Это поле обязательное</div>
      </el-form-item>

      <!-- IP specific fields -->
      <template v-if="senderType === SenderTypeEnum.IP">
        <el-form-item label="ОГРНИП">
          <el-input v-model="ogrnip" />
        </el-form-item>
      </template>

      <!-- OOO specific fields -->
      <template v-if="senderType === SenderTypeEnum.OOO">
        <el-form-item label="КПП">
          <el-input v-model="kpp" />
        </el-form-item>
        <el-form-item label="Имя генерального директора">
          <el-input v-model="ceoName" />
        </el-form-item>
      </template>

      <!-- Self Employed specific fields -->
      <template v-if="senderType === SenderTypeEnum.SELF_EMPLOYED">
        <el-form-item label="Юридический адрес">
          <el-input v-model="legalAddress" />
        </el-form-item>
        <el-form-item label="Юридический адрес (англ.)">
          <el-input v-model="legalAddressEn" />
        </el-form-item>
        <el-form-item label="Адрес банка">
          <el-input v-model="bankAddress" />
        </el-form-item>
        <el-form-item label="Адрес банка (англ.)">
          <el-input v-model="bankAddressEn" />
        </el-form-item>
        <el-form-item label="Email">
          <el-input v-model="email" type="email" />
        </el-form-item>
        <el-form-item label="Телефон">
          <el-input v-model="phone" />
        </el-form-item>
      </template>
    </div>

    <!-- Action buttons for standalone mode -->
    <div v-if="isStandalone" class="form-actions">
      <el-button @click="$emit('cancel')">Отмена</el-button>
      <el-button type="primary" @click="handleSave" :loading="loading">Сохранить</el-button>
    </div>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, onMounted } from 'vue'
import type { Sender } from '@/store/types'
import { SenderTypeEnum } from '@/store/types'
import { useStore } from 'vuex'
import { Loading } from '@element-plus/icons-vue'
import type { UploadInstance, UploadFile } from 'element-plus'

export default defineComponent({
  name: 'SenderInformation',
  components: {
    Loading
  },
  props: {
    isStandalone: {
      type: Boolean,
      default: false
    },
    initialSender: {
      type: Object as () => Sender | null,
      default: null
    }
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const store = useStore()
    const showErrors = ref(false)
    const isProcessingFile = ref(false)
    const fileList = ref<UploadFile[]>([])
    const uploadRef = ref<UploadInstance>()
    const loading = ref(false)
    
    // Initialize with default type OOO or initial sender
    if (props.initialSender) {
      store.commit('SET_SENDER_FOR_CURRENT_ORDER', props.initialSender)
    } else if (!store.state.currentOrder?.sender?.type) {
      const defaultSender = { type: SenderTypeEnum.OOO }
      store.commit('SET_SENDER_FOR_CURRENT_ORDER', defaultSender)
    }
    
    // Helper function to update sender fields
    const updateSenderField = (field: string, value: any) => {
      const updatedSender = { 
        ...(store.state.currentOrder?.sender || {}), 
        [field]: value 
      }
      store.commit('SET_SENDER_FOR_CURRENT_ORDER', updatedSender)
    }

    const senderTypeLabels = computed(() => {
      return {
        [SenderTypeEnum.OOO]: 'Юридическое лицо',
        [SenderTypeEnum.IP]: 'Индивидуальный предприниматель',
      } as Record<SenderTypeEnum, string>
    })

    const senderTypeLabelsGentiveCase = computed(() => {
      return {
        [SenderTypeEnum.OOO]: 'юридического лица',
        [SenderTypeEnum.IP]: 'индивидуального предпринимателя',
        [SenderTypeEnum.SELF_EMPLOYED]: 'самозанятого'
      } as Record<SenderTypeEnum, string>
    })

    // Computed properties with getters and setters for store connection
    const senderType = computed({
      get: () => store.state.currentOrder?.sender?.type || SenderTypeEnum.OOO,
      set: (value) => updateSenderField('type', value)
    })

    const name = computed({
      get: () => store.state.currentOrder?.sender?.name || '',
      set: (value) => updateSenderField('name', value)
    })

    const nameEn = computed({
      get: () => store.state.currentOrder?.sender?.name_en || '',
      set: (value) => updateSenderField('name_en', value)
    })

    const inn = computed({
      get: () => store.state.currentOrder?.sender?.inn || '',
      set: (value) => updateSenderField('inn', value)
    })

    const bankName = computed({
      get: () => store.state.currentOrder?.sender?.bank_name || '',
      set: (value) => updateSenderField('bank_name', value)
    })

    const bankNameEn = computed({
      get: () => store.state.currentOrder?.sender?.bank_name_en || '',
      set: (value) => updateSenderField('bank_name_en', value)
    })

    const bankBik = computed({
      get: () => store.state.currentOrder?.sender?.bank_bik || '',
      set: (value) => updateSenderField('bank_bik', value)
    })

    const bankCurrentAccount = computed({
      get: () => store.state.currentOrder?.sender?.bank_current_account || '',
      set: (value) => updateSenderField('bank_current_account', value)
    })

    const bankCorrespondentAccount = computed({
      get: () => store.state.currentOrder?.sender?.bank_correspondent_account || '',
      set: (value) => updateSenderField('bank_correspondent_account', value)
    })

    const entityAddress = computed({
      get: () => store.state.currentOrder?.sender?.entity_address || '',
      set: (value) => updateSenderField('entity_address', value)
    })

    const entityAddressEn = computed({
      get: () => store.state.currentOrder?.sender?.entity_address_en || '',
      set: (value) => updateSenderField('entity_address_en', value)
    })

    // IP specific fields
    const ogrnip = computed({
      get: () => store.state.currentOrder?.sender?.ogrnip || '',
      set: (value) => updateSenderField('ogrnip', value)
    })

    // OOO specific fields
    const kpp = computed({
      get: () => store.state.currentOrder?.sender?.kpp || '',
      set: (value) => updateSenderField('kpp', value)
    })

    const ceoName = computed({
      get: () => store.state.currentOrder?.sender?.ceo_name || '',
      set: (value) => updateSenderField('ceo_name', value)
    })

    const ceoNameGenitive = computed({
      get: () => store.state.currentOrder?.sender?.ceo_name_genitive || '',
      set: (value) => updateSenderField('ceo_name_genitive', value)
    })

    const ceoNameEn = computed({
      get: () => store.state.currentOrder?.sender?.ceo_name_en || '',
      set: (value) => updateSenderField('ceo_name_en', value)
    })

    const cfoName = computed({
      get: () => store.state.currentOrder?.sender?.cfo_name || '',
      set: (value) => updateSenderField('cfo_name', value)
    })

    const cfoNameGenitive = computed({
      get: () => store.state.currentOrder?.sender?.cfo_name_genitive || '',
      set: (value) => updateSenderField('cfo_name_genitive', value)
    })

    const cfoNameEn = computed({
      get: () => store.state.currentOrder?.sender?.cfo_name_en || '',
      set: (value) => updateSenderField('cfo_name_en', value)
    })

    // Self Employed specific fields
    const legalAddress = computed({
      get: () => store.state.currentOrder?.sender?.legal_address || '',
      set: (value) => updateSenderField('legal_address', value)
    })

    const legalAddressEn = computed({
      get: () => store.state.currentOrder?.sender?.legal_address_en || '',
      set: (value) => updateSenderField('legal_address_en', value)
    })

    const bankAddress = computed({
      get: () => store.state.currentOrder?.sender?.bank_address || '',
      set: (value) => updateSenderField('bank_address', value)
    })

    const bankAddressEn = computed({
      get: () => store.state.currentOrder?.sender?.bank_address_en || '',
      set: (value) => updateSenderField('bank_address_en', value)
    })

    const email = computed({
      get: () => store.state.currentOrder?.sender?.email || '',
      set: (value) => updateSenderField('email', value)
    })

    const phone = computed({
      get: () => store.state.currentOrder?.sender?.phone || '',
      set: (value) => updateSenderField('phone', value)
    })

    // Watch for validation
    watch(() => store.getters.isSenderInformationValid, (isValid) => {
      if (!isValid) {
        showErrors.value = true
      }
    })

    // Direct store-based computed property for selected sender
    const selectedSenderComputed = computed<Sender | null>({
      get: () => {
        const senderId = store.state.currentOrder?.sender_id
        if (senderId) {
          return store.state.senders.find((sender: Sender) => sender.id === senderId) || null
        }
        return null  // This will select "Новый отправитель" by default
      },
      set: (value: Sender | null) => {
        if (value) {
          store.commit('SET_SENDER_FOR_CURRENT_ORDER', value)
          store.commit('UPDATE_ORDER_FIELD', { field: 'sender_id', value: value.id })
        } else {
          store.commit('UPDATE_ORDER_FIELD', { field: 'sender_id', value: null })
        }
      }
    })

    const sendersList = computed(() => store.state.senders)

    // Before upload handler to clear previous files
    const beforeUpload = (file: File) => {
      // Clear the file list before adding a new file
      fileList.value = []
      return true
    }

    // Handle file upload for automatic parsing
    const handleFileChange = (uploadFile: UploadFile) => {
      // Make sure we're only keeping the latest file
      fileList.value = [uploadFile]
      
      if (uploadFile && uploadFile.raw) {
        // Set loading state
        isProcessingFile.value = true
        
        // Send the file to the backend for processing
        store.dispatch('parseSenderDocument', uploadFile.raw)
          .then(() => {
            isProcessingFile.value = false
          })
          .catch(error => {
            console.error('Error parsing document:', error)
            isProcessingFile.value = false
          })
      }
    }
    
    // Handle file removal
    const handleRemoveFile = () => {
      fileList.value = []
    }

    // Handle save in standalone mode
    const handleSave = () => {
      if (!store.getters.isSenderInformationValid) {
        showErrors.value = true
        return
      }
      emit('save', store.state.currentOrder.sender)
    }

    return {
      senderType,
      selectedSenderComputed,
      showErrors,
      sendersList,
      SenderTypeEnum,
      name,
      nameEn,
      inn,
      bankName,
      bankNameEn,
      bankBik,
      bankCurrentAccount,
      bankCorrespondentAccount,
      entityAddress,
      entityAddressEn,
      ogrnip,
      kpp,
      ceoName,
      ceoNameGenitive,
      ceoNameEn,
      cfoName,
      cfoNameGenitive,
      cfoNameEn,
      legalAddress,
      legalAddressEn,
      bankAddress,
      bankAddressEn,
      email,
      phone,
      handleFileChange,
      handleRemoveFile,
      beforeUpload,
      fileList,
      uploadRef,
      senderTypeLabels,
      senderTypeLabelsGentiveCase,
      isProcessingFile,
      loading,
      handleSave
    }
  }
})
</script>

<style scoped>
.selection-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.selection-item {
  flex: 1;
  min-width: 0;
}

.sender-fields {
  margin-top: 10px;
}

.upload-container {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.loading-icon {
  font-size: 24px;
  color: #409EFF;
  animation: rotating 2s linear infinite;
}

.loading-text {
  margin-top: 8px;
  color: #606266;
}

.el-upload__tip {
  line-height: 1.5;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
:root {
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

</style> 